import { jsx as _jsx } from "react/jsx-runtime";
import { EventType, Logger, } from '@azure/msal-browser';
// @azure/msal-browser-1p automatically forward some telemetry to 1ds
// we only need to use it when instantiating PublicClientApplication
// otherwise, import everything else from @azure/msal-browser
import { PublicClientApplication } from '@azure/msal-browser-1p';
import { MsalProvider } from '@azure/msal-react';
import { AuthPrompt, BROWSER_STORAGE, DEFAULT_SCOPES, getNormalizedScopes, LOG_PREFIX } from '../../../utilities';
import { getBrowserStorage } from '../../../utilities/getBrowserStorage';
import { AuthErrorEnum, AuthImplementationEnum, CacheLocation } from '../../types';
import { AsyncQueue } from './AsyncQueue';
import { AuthError } from './error/AuthError';
import { getMsalConfig } from './getMsalConfig';
let auth;
/**
 * Return a singleton auth instance which implements the IAuth interface
 * This implementation uses MSAL under the hood
 *
 * @param authOptions.authority Authentication authority
 * @param authOptions.clientId clientId of this app. Also known as application id
 * @param authOptions.cacheLocation browser location to store id token, access tokens and refresh token, default to local storage
 * @param reporter Pass object that conforms to this interface to be notified of auth error and other events
 */
export const getMsalAuth = ({ authority, clientId, cacheLocation = CacheLocation.LocalStorage, redirectUriForHiddenIframeFlow }, reporter) => {
    if (!auth) {
        if (!authority) {
            throw new Error('Please provide an authority endpoint');
        }
        if (!authority.endsWith('common/')) {
            throw new Error('Authority endpoint must end in common/');
        }
        const pbca = new PublicClientApplication(getMsalConfig(authority, clientId, cacheLocation, reporter));
        auth = new MsalAuth(pbca, redirectUriForHiddenIframeFlow);
    }
    return auth;
};
export class MsalAuth {
    constructor(pbca, redirectUriForHiddenIframeFlow) {
        Object.defineProperty(this, "_clientId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "_user", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "_lastestAuthEventType", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "_authority", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "_azureInstance", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "_pbca", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "_logger", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "_browserStorage", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "_redirectUriForHiddenIframeFlow", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "_acquireTokenTaskQueue", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        // intentionally not made private so that getMsalAuth.test.ts can spy on it without running into typescript issue
        Object.defineProperty(this, "_originalAcquireTokenSilent", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "_isAuthInteractiveInProgress", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this._pbca = pbca;
        // store the original acquireTokenSilent method so that we can proxy it and call it when needed
        this._originalAcquireTokenSilent = pbca.acquireTokenSilent.bind(pbca);
        this._redirectUriForHiddenIframeFlow = redirectUriForHiddenIframeFlow;
        this._logger = this._pbca.getLogger();
        this._acquireTokenTaskQueue = new AsyncQueue();
        this._isAuthInteractiveInProgress = false;
        // Add a proxy version of pbca.acquireTokenSilent to the pbca object so that
        // when useMsalAuthentication() hook in MSAL sdk call this method, it will be added to the queue automatically, and will be
        // called in a serial manner along with other acquireToken calls
        pbca.acquireTokenSilent = async (request) => {
            this.logInfo(`getMsalAuth: Calling proxy version of pbca.acquireTokenSilent. 
        Enqueuing call to original pbca.acquireTokenSilent. Request: ${JSON.stringify(request.scopes)}`);
            return this._acquireTokenTaskQueue.enqueue(() => {
                this.logInfo(`getMsalAuth: Calling original pbca.acquireTokenSilent with ${JSON.stringify(request.scopes)}`);
                return this._originalAcquireTokenSilent(request);
            });
        };
        const { auth, cache } = this._pbca.getConfiguration();
        this._authority = auth.authority;
        this._clientId = auth.clientId;
        this._browserStorage = getBrowserStorage(cache.cacheLocation);
        // extract the azureInstance from this authority endpoint
        this._azureInstance = this._authority.substring(0, this._authority.indexOf('common/'));
        this._user = this.convertAccountInfoToUser(this._pbca.getActiveAccount());
        this.trackMsalEvents();
    }
    /**
     * Return auth implementation type
     */
    get authImplementationType() {
        return AuthImplementationEnum.MSAL;
    }
    /**
     * Return clientId used to create this auth instance
     * clientId is the same thing as appId
     */
    get clientId() {
        return this._clientId;
    }
    /**
     * Return the authenticated user
     */
    get user() {
        return this._user;
    }
    getActiveAccount() {
        return this._pbca.getActiveAccount();
    }
    get isAuthInteractiveInProgress() {
        this.logInfo(`getMsalAuth: calling isAuthInteractiveInProgress getter. its state is: ${this._isAuthInteractiveInProgress}`);
        return this._isAuthInteractiveInProgress;
    }
    set isAuthInteractiveInProgress(newState) {
        this.logInfo(`getMsalAuth: calling isAuthInteractiveInProgress setter. setting its state to ${newState}`);
        this._isAuthInteractiveInProgress = newState;
    }
    createNeverResolvedPromise(message) {
        message && this.logInfo(message);
        return new Promise(() => { });
    }
    getLatestAccountFromStorage() {
        try {
            this.logInfo('retrieving latest account from storage');
            const storedContent = this._browserStorage.getItem(BROWSER_STORAGE.LATEST_ACCOUNT);
            if (storedContent)
                return JSON.parse(storedContent);
            return null;
        }
        catch (e) {
            this.logError(AuthErrorEnum.OTHER_AUTH_ERROR, { message: 'failed to retrieve latest account from storage', e });
            return null;
        }
    }
    setActiveAccount(account) {
        this._user = this.convertAccountInfoToUser(account);
        return this._pbca.setActiveAccount(account);
    }
    getAllAccounts() {
        return this._pbca.getAllAccounts();
    }
    /**
     * Return the latest MSAL auth event type
     */
    get latestAuthEventType() {
        return this._lastestAuthEventType;
    }
    /**
     * Trigger a login flow via redirect
     * @param loginOption
     */
    async login(loginOption) {
        loginOption = loginOption ?? {};
        const request = {
            ...loginOption,
            scopes: loginOption.scopes ?? DEFAULT_SCOPES,
            authority: loginOption.authority ?? this.getAuthority(loginOption.tenantId || loginOption.domainHint)
        };
        // set account to an empty object so that current active account wont be use by MSAL to interfere with this request
        if (request.prompt === AuthPrompt.LOGIN
            || request.prompt === AuthPrompt.SELECT_ACCOUNT
            || request.loginHint
            || request.tenantId
            || request.domainHint) {
            request.account = {};
        }
        try {
            this.logInfo(`getMsalAuth: calling login method with ${JSON.stringify(request)}`);
            if (this.isAuthInteractiveInProgress) {
                return await this.createNeverResolvedPromise('auth interaction in progress. skipping loginRedirect call');
            }
            this.isAuthInteractiveInProgress = true;
            return await this._pbca.loginRedirect(request);
        }
        catch (e) {
            this.logError(AuthErrorEnum.LOGIN_ERROR, e);
            if (AuthError.isInteractionInProgressError(e)) {
                this.logError(AuthErrorEnum.LOGIN_ERROR, {
                    message: 'Start returning promise that never resolves in login method'
                });
                return await this.createNeverResolvedPromise();
            }
            throw e;
        }
        finally {
            this.isAuthInteractiveInProgress = false;
        }
    }
    /**
     * Trigger a logout flow via redirect
     * @param logoutOption
     */
    async logout(logoutOption) {
        try {
            this.logInfo(`getMsalAuth: calling logout method with ${logoutOption}`);
            if (this.isAuthInteractiveInProgress) {
                return await this.createNeverResolvedPromise('auth interaction in progress. skipping logoutRedirect call');
            }
            this.isAuthInteractiveInProgress = true;
            this.removeLatestAccount();
            return await this._pbca.logoutRedirect(logoutOption);
        }
        catch (e) {
            this.logError(AuthErrorEnum.LOGOUT_ERROR, e);
            if (AuthError.isInteractionInProgressError(e)) {
                this.logError(AuthErrorEnum.LOGOUT_ERROR, {
                    message: 'Start returning promise that never resolves in logout method'
                });
                return await this.createNeverResolvedPromise();
            }
            throw e;
        }
        finally {
            this.isAuthInteractiveInProgress = false;
        }
    }
    /**
     * Silently retrieve access token for this resource from local cache if it is still valid.
     * If the cached access token is already expired, it will attempt to get a new one via unexpired refresh token
     * If the refresh token is also expired, it will attempt a new authentication flow using a hidden iframe and exchange
     * the authorization code for a new idToken, refreshToken and accessToken. The new refresh token will then be used
     * to get an accessToken for this resource.
     *
     * Note that refresh token is multi-resource as in it can be used to get a new accessToken for any resources.
     * AccessToken is not multi-resource. It can be created and used for one resource only (multi scopes are okay
     * as long as it is for one resource)
     * In the event that a user interaction is required (MFA, conditonal access related enforcement, etc...),
     * it will fallback to a redirect flow
     *
     * @param acquireTokenOption
     */
    async acquireToken(acquireTokenOption) {
        this.logInfo(`getMsalAuth: calling acquireToken method. Enqueuing acquireTokenHelper call for: ${JSON.stringify(acquireTokenOption)}`);
        return this._acquireTokenTaskQueue.enqueue(() => this.acquireTokenHelper(acquireTokenOption));
    }
    async acquireTokenHelper(acquireTokenOption) {
        const normalizedScopes = getNormalizedScopes(acquireTokenOption?.scopes) ?? [];
        const request = {
            // by default, use tenanted authority based on the current active account if possible.
            // if not default to /common or whatever the user specify in the _request
            authority: this.getAuthority(this._pbca.getActiveAccount()?.tenantId ?? acquireTokenOption?.account?.tenantId),
            ...acquireTokenOption,
            scopes: [...normalizedScopes]
        };
        try {
            const newRequest = this._redirectUriForHiddenIframeFlow ? {
                ...request,
                redirectUri: this._redirectUriForHiddenIframeFlow
            } : request;
            this.logInfo(`getMsalAuth: calling acquireTokenHelper method ${JSON.stringify(newRequest)}`);
            return (await this._originalAcquireTokenSilent(newRequest)).accessToken;
        }
        catch (error) {
            if (AuthError.isSpecialMyAppLogic(error, normalizedScopes, this.clientId)) {
                this.logError(AuthErrorEnum.ACQUIRE_TOKEN_ERROR, {
                    e: error,
                    message: `InteractionRequiredAuthError related to IAMUX_APP_ID or APPMANAGEMENT_APP_ID.
           This is a known limitation so it is okay to ignore this`
                });
                throw error;
            }
            if (AuthError.isCABlockAccessError(error)) {
                this.logError(AuthErrorEnum.ACQUIRE_TOKEN_ERROR, {
                    e: error,
                    message: 'calling acquireTokenHelper method got blocked by CA policy'
                });
                throw error;
            }
            if (AuthError.isInteractionRequiredError(error)) {
                this.logInfo(`getMsalAuth: calling acquireTokenRedirectFlow method because interactionRequired
         error was encountered ${JSON.stringify(request)}`);
                return this.acquireTokenRedirectFlow(request);
            }
            this.logInfo(`getMsalAuth: calling acquireTokenRedirectFlow method as last resort
       due to unknown error: ${JSON.stringify(error)}. Request: ${JSON.stringify(request)}`);
            return this.acquireTokenRedirectFlow(request);
        }
    }
    async acquireTokenRedirectFlow(request) {
        if (this.isAuthInteractiveInProgress) {
            return this.createNeverResolvedPromise('auth interaction in progress. skipping acquireTokenRedirect call');
        }
        this.isAuthInteractiveInProgress = true;
        await this._pbca
            .acquireTokenRedirect({
            ...request
        })
            .catch(async (e) => {
            this.logError(AuthErrorEnum.ACQUIRE_TOKEN_ERROR, e);
            // when the consumer is not being careful when calling interactive api by checking (inProgress !== AuthInteractionStatusEnum.none) first
            // it is possible for the consumer to run into issue where interactive api(acquireRedirect, loginRedirect) are called from multiple areas
            // simultaneously which can cause MSAl to get into a bad newState. Here we catch that issue and allowing one to go through
            if (AuthError.isInteractionInProgressError(e)) {
                this.logError(AuthErrorEnum.ACQUIRE_TOKEN_ERROR, {
                    e,
                    message: 'Start returning promise that never resolves in acquireTokenRedirectFlow method'
                });
                return this.createNeverResolvedPromise('auth interaction in progress. This case should no longer be possible.');
            }
        }).finally(() => {
            this.isAuthInteractiveInProgress = false;
        });
        this.logInfo(`getMsalAuth: calling acquireTokenRedirectFlow method. Returning 'This case is not possible due to redirect'`);
        return 'This case is not possible due to redirect';
    }
    /**
     * authorityHint can be a tenantId or a domain name. If provided, return a tenanted authority. Otherwise, return the default authority
     * @param authorityHint
     */
    getAuthority(authorityHint) {
        return authorityHint ? `${this._azureInstance}${authorityHint}/` : this._authority;
    }
    /**
     * Override MSAL default NavigationClient functionality.
     * This can be used to support client-side routing after the authentication phase completes
     */
    setNavigationClient(navigationClient) {
        this._pbca.setNavigationClient(navigationClient);
    }
    /**
     * Return React Provider for this Auth implementation
     * @param children
     */
    getProvider(children) {
        return _jsx(MsalProvider, { instance: this._pbca, children: children });
    }
    /**
     * Track all MSAL events and hand it off to handlers for appropriate usage
     */
    trackMsalEvents() {
        this._pbca.addEventCallback((event) => {
            this.trackLatestAuthEventType(event);
            this.trackNewActiveAccount(event);
        });
    }
    /**
     * Track latest auth event type
     * @param event
     */
    trackLatestAuthEventType(event) {
        this._lastestAuthEventType = event.eventType;
    }
    /**
     * Track current active account
     * @param event
     */
    trackNewActiveAccount(event) {
        // When signing in a new account, LOGIN_SUCCESS will occurs when the number of loggedInAccounts is bigger
        // than before this auth flow occurs
        // When switching accounts or tenants, it triggers an acquire ACQUIRE_TOKEN_SUCCESS instead of a LOGIN_SUCCESS
        // since the number of loggedInAccounts doesn't change but we also want to set a new active account based on that
        // accounts/tenants switch
        if (event.payload &&
            (event.eventType === EventType.LOGIN_SUCCESS ||
                event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS)) {
            const payload = event.payload;
            const account = payload.account;
            if (account) {
                this._user = this.convertAccountInfoToUser(account);
                this.storeLatestAccount(account);
                const message = `getMsalAuth: Track new active account ${account.tenantId}, 
          eventType: ${event.eventType}, interactionType: ${event.interactionType}`;
                this.logInfo(message);
                this._pbca.setActiveAccount(account);
            }
        }
    }
    /**
     * Convert MSAL accountInfo object to IAuthUser object that can be consumed directly by My* applications
     * @param accountInfo
     */
    convertAccountInfoToUser(accountInfo) {
        if (!accountInfo)
            return null;
        const idTokenClaims = accountInfo.idTokenClaims;
        return {
            tenantId: idTokenClaims?.tid ?? '',
            // homeAccountId is {uniqueId}.{tenantId}
            // uniqueId: accountId of this user within the home tenant
            // tenantId: home tenantId of this user
            homeTenantId: accountInfo.homeAccountId.split('.')[1],
            objectId: idTokenClaims?.oid ?? '',
            rawClaims: idTokenClaims ?? {},
            username: accountInfo.username ?? '',
            email: idTokenClaims?.email,
            name: accountInfo.name
        };
    }
    storeLatestAccount(account) {
        try {
            this.logInfo('getMsalAuth: storing latest account to browser storage');
            this._browserStorage.setItem(BROWSER_STORAGE.LATEST_ACCOUNT, JSON.stringify(account));
        }
        catch (e) {
            this.logError(AuthErrorEnum.OTHER_AUTH_ERROR, { message: 'failed to store latest account to browser storage', e });
        }
    }
    removeLatestAccount() {
        try {
            this.logInfo('getMsalAuth: removing latest account from browser storage');
            this._browserStorage.removeItem(BROWSER_STORAGE.LATEST_ACCOUNT);
        }
        catch (e) {
            this.logError(AuthErrorEnum.OTHER_AUTH_ERROR, { message: 'failed to remove latest account from browser storage', e });
        }
    }
    /**
     * Use MSAL logger to log info level message with our own prefix
     * @param message
     */
    logInfo(message) {
        const content = `${LOG_PREFIX}, message: ${message}`;
        this._logger.info(content);
    }
    /**
     * Use MSAL logger to log error level message with our own prefix
     * @param errorType
     * @param error
     */
    logError(errorType, error) {
        try {
            const content = `${LOG_PREFIX}, error type: ${errorType}, error: ${JSON.stringify(error)}`;
            this._logger.error(content);
        }
        catch (e) {
            console.log(e);
        }
    }
}
//# sourceMappingURL=getMsalAuth.js.map